import { PRODUCTS_CONFIG } from './actionTypes';
import { createSelector } from '@reduxjs/toolkit';

export const selectProductsState = (state) => state.products;

export const selectProductsById = (state) => state.products.byId;

export const selectAllProductIds = (state) => state.products.allIds;

export const selectProductItems = (state) => state.products.items;

export const selectIsListLoading = (state) => state.products.loading.list;

export const selectIsItemLoading = (state, id) => state.products.loading.items[id] || false;

export const selectIsAnyLoading = (state) => {
    const { list, items } = state.products.loading;
    return list || Object.values(items).some(loading => loading);
};

export const selectListError = (state) => state.products.error.list;

export const selectItemError = (state, id) => state.products.error.items[id] || null;

export const selectHasErrors = (state) => {
    const { list, items } = state.products.error;
    return !!list || Object.values(items).some(error => error);
};

export const selectCurrentPage = (state) => state.products.pagination.currentPage;

export const selectTotalPages = (state) => state.products.pagination.totalPages;

export const selectTotal = (state) => state.products.pagination.total;

export const selectHasMore = (state) => state.products.pagination.hasMore;

export const selectPaginationInfo = (state) => ({
    currentPage: state.products.pagination.currentPage,
    totalPages: state.products.pagination.totalPages,
    total: state.products.pagination.total,
    hasMore: state.products.pagination.hasMore,
    loaded: state.products.allIds.length,
});

export const makeSelectProductsByCategory = () =>
    createSelector(
        [(state) => state.products.byId, (state) => state.products.categoriesMap, (_state, categoryId) => categoryId],
        (byId, categoriesMap, categoryId) => {
            const productIds = categoriesMap[categoryId] || [];
            return productIds.map(id => byId[id]).filter(Boolean);
        }
    );

export const selectProductsByCategory = (state, categoryId) => {
    const { byId, categoriesMap } = state.products;
    const productIds = categoriesMap[categoryId] || [];
    return productIds.map(id => byId[id]).filter(Boolean);
};

export const selectIsCategoryLoading = (state, categoryId) => {
    return state.products.loading.categories[categoryId] || false;
};

export const selectCategoryError = (state, categoryId) => {
    return state.products.error.categories[categoryId] || null;
};

export const selectIsCategoryLoaded = (state, categoryId) => {
    return state.products.categoriesLoaded[categoryId]?.loaded || false;
};

export const selectCategoryHasMore = (state, categoryId) => {
    const categoriesLoaded = state.products?.categoriesLoaded;
    return categoriesLoaded?.[categoryId]?.hasMore || false;
};

export const selectCategoryCurrentPage = (state, categoryId) => {
    const categoriesLoaded = state.products?.categoriesLoaded;
    return categoriesLoaded?.[categoryId]?.currentPage || 1;
};

export const selectCategoryTotalPages = (state, categoryId) => {
    const categoriesLoaded = state.products?.categoriesLoaded;
    return categoriesLoaded?.[categoryId]?.totalPages || 0;
};

export const selectCategoryTotal = (state, categoryId) => {
    const categoriesLoaded = state.products?.categoriesLoaded;
    return categoriesLoaded?.[categoryId]?.total || 0;
};

export const selectCanLoadMoreCategory = (state, categoryId) => {
    const hasMore = selectCategoryHasMore(state, categoryId);
    const isLoading = selectIsCategoryLoading(state, categoryId);
    return hasMore && !isLoading;
};

export const selectCategoryPaginationInfo = (state, categoryId) => {
    const categoriesLoaded = state.products?.categoriesLoaded;
    const categoryInfo = categoriesLoaded?.[categoryId];
    const loadedCount = selectProductsByCategory(state, categoryId).length;

    return {
        currentPage: categoryInfo?.currentPage || 1,
        totalPages: categoryInfo?.totalPages || 0,
        total: categoryInfo?.total || 0,
        hasMore: categoryInfo?.hasMore || false,
        loaded: loadedCount,
        perPage: categoryInfo?.perPage || PRODUCTS_CONFIG.SHOWCASE_SIZE,
    };
};

const EMPTY_CATEGORY_STATE = {
    items: [],
    isLoading: false,
    error: null,
    isLoaded: false,
    isEmpty: true,
    hasMore: false,
    canLoadMore: false,
    paginationInfo: {},
};

export const makeSelectCategoryState = () => {
    const selectItems = makeSelectProductsByCategory();
    return createSelector(
        [
            selectItems,
            (state, categoryId) => selectIsCategoryLoading(state, categoryId),
            (state, categoryId) => selectCategoryError(state, categoryId),
            (state, categoryId) => selectIsCategoryLoaded(state, categoryId),
            (state, categoryId) => selectCategoryHasMore(state, categoryId),
            (state, categoryId) => selectCanLoadMoreCategory(state, categoryId),
            (state, categoryId) => selectCategoryPaginationInfo(state, categoryId),
        ],
        (items, isLoading, error, isLoaded, hasMore, canLoadMore, paginationInfo) => ({
            items,
            isLoading,
            error,
            isLoaded,
            isEmpty: !isLoading && items.length === 0,
            hasMore,
            canLoadMore,
            paginationInfo,
        })
    );
};

export const selectCategoryState = (state, categoryId) => {
    if (!state.products) return EMPTY_CATEGORY_STATE;
    return {
        items: selectProductsByCategory(state, categoryId),
        isLoading: selectIsCategoryLoading(state, categoryId),
        error: selectCategoryError(state, categoryId),
        isLoaded: selectIsCategoryLoaded(state, categoryId),
        isEmpty: !selectIsCategoryLoading(state, categoryId) &&
            selectProductsByCategory(state, categoryId).length === 0,
        hasMore: selectCategoryHasMore(state, categoryId),
        canLoadMore: selectCanLoadMoreCategory(state, categoryId),
        paginationInfo: selectCategoryPaginationInfo(state, categoryId),
    };
};

export const selectFilters = (state) => state.products.filters;

export const selectSearchText = (state) => state.products.filters.search;

export const selectCategoryId = (state) => state.products.filters.categoryId;

export const selectIsFiltered = (state) => {
    const filters = state.products.filters;
    return !!(filters.search || filters.categoryId);
};

export const selectProductById = (state, id) => state.products.byId[id] || null;

export const selectAllProducts = (state) => {
    const { byId, allIds } = state.products;
    return allIds.map(id => byId[id]).filter(Boolean);
};

export const selectProductsInStock = (state) => {
    const products = selectAllProducts(state);
    return products.filter(product => product.stock_status === 'instock');
};

export const selectFeaturedProducts = (state) => {
    const products = selectAllProducts(state);
    return products.filter(product => product.featured === true);
};

export const selectProductsOnSale = (state) => {
    const products = selectAllProducts(state);
    return products.filter(product => product.on_sale === true);
};

export const selectProductsListState = (state) => ({
    items: selectAllProducts(state),
    isLoading: selectIsListLoading(state),
    error: selectListError(state),
    hasMore: selectHasMore(state),
    currentPage: selectCurrentPage(state),
    total: selectTotal(state),
    isEmpty: state.products.allIds.length === 0 && !selectIsListLoading(state),
    isFiltered: selectIsFiltered(state),
    filters: selectFilters(state),
});

export const selectShouldShowInitialLoading = (state) => {
    return selectIsListLoading(state) &&
        state.products.pagination.currentPage === 1 &&
        state.products.allIds.length === 0;
};

export const selectShouldShowLoadMoreSpinner = (state) => {
    return selectIsListLoading(state) && state.products.pagination.currentPage > 1;
};

export const selectCanLoadMore = (state) => {
    return selectHasMore(state) && !selectIsListLoading(state);
};

export const selectProductsStats = (state) => ({
    total: selectTotal(state),
    loaded: state.products.allIds.length,
    hasData: state.products.allIds.length > 0,
    lastUpdated: state.products.lastUpdated,
    isStale: state.products.lastUpdated
        ? Date.now() - state.products.lastUpdated > 5 * 60 * 1000
        : true,
    inStock: selectProductsInStock(state).length,
    onSale: selectProductsOnSale(state).length,
    featured: selectFeaturedProducts(state).length,
});

// Related products
export const selectRelatedProducts = (state, productId) => {
    const ids = state.products.related[productId]?.items || [];
    return ids.map(id => state.products.byId[id]).filter(Boolean);
};

export const selectIsRelatedLoading = (state, productId) => state.products.related[productId]?.loading || false;

export const selectSearchState = (state) => state.products.search;

export const selectSearchResults = (state) => state.products.search.items;

export const selectIsSearchLoading = (state) => state.products.search.loading;

export const productsSelectors = {
    selectProductsState,
    selectProductsById,
    selectAllProductIds,
    selectProductItems,

    selectIsListLoading,
    selectIsItemLoading,
    selectIsAnyLoading,

    selectProductsByCategory,
    makeSelectProductsByCategory,
    selectIsCategoryLoading,
    selectCategoryError,
    selectIsCategoryLoaded,
    selectCategoryState,
    makeSelectCategoryState,
    selectCategoryHasMore,
    selectCategoryCurrentPage,
    selectCategoryTotalPages,
    selectCategoryTotal,
    selectCanLoadMoreCategory,
    selectCategoryPaginationInfo,

    selectListError,
    selectItemError,
    selectHasErrors,

    selectCurrentPage,
    selectTotalPages,
    selectTotal,
    selectHasMore,
    selectPaginationInfo,

    selectFilters,
    selectSearchText,
    selectCategoryId,
    selectIsFiltered,

    selectProductById,
    selectAllProducts,
    selectProductsInStock,
    selectFeaturedProducts,
    selectProductsOnSale,

    // UI
    selectProductsListState,
    selectShouldShowInitialLoading,
    selectShouldShowLoadMoreSpinner,
    selectCanLoadMore,
    selectProductsStats,

    // Related
    selectRelatedProducts,
    selectIsRelatedLoading,

    selectSearchState,
    selectSearchResults,
    selectIsSearchLoading
};

export default productsSelectors;
